// Minimal markdown-lite renderer for Maeve's own chat responses.
//
// The model (Gemini) reaches for standard markdown — `**bold**`, `- `/`* `
// bullet lists, `1. ` numbered lists — when a structured answer reads better
// that way (e.g. "Ask Maeve" surfacing a case list). `ChatBubble` previously
// rendered every turn as a raw text node, so that markdown showed up as
// literal asterisks instead of formatting.
//
// Deliberately NOT a full markdown library: this app has zero
// `dangerouslySetInnerHTML` anywhere (grepped), and model output is
// externally-influenced content rendered to every persona — pulling in an
// HTML-string-producing markdown lib would be the first place that pattern
// broke. This parses a small whitelisted subset directly into React
// elements (JSX composition, never `innerHTML`), so untrusted text stays as
// inert React children exactly like before — only the display differs.
//
// Only applied to `role="assistant"` turns in `ChatBubble` — a user's own
// typed text is shown exactly as they wrote it, never reinterpreted.
//
// Line-by-line grouping (not whole-block matching): a real answer commonly
// mixes prose and a list with no blank line between them ("Here's what's
// overdue:\n* item\n* item\nLet me know if..."). Consecutive same-kind
// lines group into one list/paragraph; a kind change or blank line closes
// the current group and starts the next — the same shape CommonMark uses,
// scoped to just the three constructs Maeve actually produces.

import type { ReactNode } from 'react';

// Leading whitespace tolerated (`^\s*`) — the model sometimes indents a
// sub-list under a heading item; this flattens it to the same single list
// level rather than leaving the marker unrecognised and rendered literally
// (e.g. "  * item" showing up as the raw text "* item").
const BULLET_RE = /^\s*[*-]\s+(.*)$/;
const NUMBERED_RE = /^\s*(\d+)\.\s+(.*)$/;

// Inline `**bold**` spans within a single line. Non-greedy, no nesting —
// matches the model's actual usage (label emphasis), not full CommonMark.
function renderInline(line: string, keyPrefix: string): ReactNode[] {
  const parts = line.split(/(\*\*[^*]+\*\*)/g).filter((p) => p !== '');
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
}

type GroupKind = 'paragraph' | 'ul' | 'ol';
interface Group {
  kind: GroupKind;
  /** Raw line text with the list marker (if any) already stripped. */
  lines: string[];
  /** 'ol' only — the source's own leading number on its first line (e.g. the
   * "3" in "3. Confirm the return date"), so a list continuing a sequence
   * renders with its real numbers instead of always restarting at 1. */
  startNumber?: number;
}

function classify(line: string): { kind: GroupKind; text: string; number?: number } {
  const bullet = line.match(BULLET_RE);
  if (bullet) return { kind: 'ul', text: bullet[1]! };
  const numbered = line.match(NUMBERED_RE);
  if (numbered) return { kind: 'ol', text: numbered[2]!, number: Number(numbered[1]) };
  return { kind: 'paragraph', text: line };
}

function renderGroup(group: Group, key: number): ReactNode {
  if (group.kind === 'ul' || group.kind === 'ol') {
    const Tag = group.kind;
    return (
      <Tag
        key={key}
        className={
          group.kind === 'ul' ? 'list-disc space-y-0.5 pl-5' : 'list-decimal space-y-0.5 pl-5'
        }
        start={group.kind === 'ol' ? group.startNumber : undefined}
      >
        {group.lines.map((text, i) => (
          <li key={i}>{renderInline(text, `${key}-${i}`)}</li>
        ))}
      </Tag>
    );
  }
  return (
    <p key={key}>
      {group.lines.map((text, i) => (
        <span key={i}>
          {renderInline(text, `${key}-${i}`)}
          {i < group.lines.length - 1 ? <br /> : null}
        </span>
      ))}
    </p>
  );
}

export function renderMaeveMarkdown(text: string): ReactNode {
  const groups: Group[] = [];
  let current: Group | null = null;

  for (const rawLine of text.split('\n')) {
    if (rawLine.trim() === '') {
      current = null; // blank line always breaks the current group
      continue;
    }
    const { kind, text: lineText, number } = classify(rawLine);
    if (current && current.kind === kind) {
      current.lines.push(lineText);
    } else {
      current =
        number === undefined
          ? { kind, lines: [lineText] }
          : { kind, lines: [lineText], startNumber: number };
      groups.push(current);
    }
  }

  // Vertical rhythm between blocks — Tailwind Preflight zeroes default p/ul/ol
  // margins, so without this, a reply mixing prose and a list (a common shape,
  // e.g. "Here's what's overdue:\n\n- item\n\nLet me know...") renders with
  // its paragraphs and list flush against each other.
  return <div className="space-y-2">{groups.map((group, i) => renderGroup(group, i))}</div>;
}
